import { defineStore, acceptHMRUpdate } from 'pinia'
import { useShopifyClient } from '#imports'
import {
  createCartQuery,
  type CartCreateResponse,
  cartAttributesUpdate,
  type CartAttributesUpdateResponse,
  addLines,
  type CartLinesAddResponse,
  removeLines,
  type CartLinesRemoveResponse,
  updateLines,
  type CartLinesUpdateResponse,
  cartDiscountCodesUpdate,
  type CartDiscountCodesUpdateResponse,
  cartGiftCardCodesUpdate,
  type CartGiftCardCodesUpdateResponse,
} from '~/graphql/shopify/mutations/cart'

import { getGiftCardProduct, type GetGiftCardProductResponse } from '~/graphql/shopify/queries/product'

import type { AutomaticDiscount, DiscountCodeBasic } from '~/graphql/shopify/queries/automaticDiscounts'
import { getCart } from '~/graphql/shopify/queries/cart'
import countries from '~/assets/constants/shopify/countries.json'
import { isGlobaleManagedMarket } from '~/assets/js/helpers/globale'

import type { Cart, CartResponse } from '~/graphql/shopify/fragments/cart'
import type { CartLine } from '~/graphql/shopify/fragments/cartLines'

import preBuiltAutomaticDiscounts from '~/assets/constants/shopify/automatic-discounts.json'
import preBuiltAutomaticCodeDiscounts from '~/assets/constants/shopify/code-discounts.json'

import {
  getCouponCode,
  getAllValidGiftDiscounts,
  isDiscountAvailableInMarket,
  isValidGiftCard,
  buildLineIdsToRemove,
  buildCartItemsWithBundleDisplayOrder,
  getBundleDisplayOrder,
  getBundleSkuFromBundleItems,
  getBundleCheckoutInfo,
  getBundleDespatchDate,
  getBundleSkusFromBundleItems,
  getBundleBaseSkuFromBundleItems,
  getBundleCost,
  getOriginalBundleCost,
  getBundleQuantity,
  getExpectedCartAttributes,
  captureException,
  getAllDiscountsFromProduct,
  getDiscountsFromCartItems,
  getUniqueBundleIdsFromNewLineItems,
  getExistingBundlesInCartFromSku,
  getBundleIsGiftCard,
  getBundleUpsellItem,
  getBundleIsPrescription,
  getParentBundleSku,
  getParentBundleBaseSku,
  getUsesTheme,
  getBundleExpectedFree,
  getMisfiredFreeGiftBundles,
  findExpectedFreeDiscountForGiftBundle,
  getDisqualifiedCouponCodes,
  type CartIDRecord,
  type CartAttributes,
  type Bundle,
  type CouponResponse,
  type GiftCard,
  getMissingCartCodesFromAvailableDiscounts,
} from '~/assets/js/helpers/shopify'
import { autoAppliedDiscountCodesQuery } from '~/gql/autoAppliedDiscountCodes'
import type { Location } from '~/shared/types'

export interface AutomaticCodeDiscountWithQualifyingProducts extends DiscountCodeBasic {
  qualifyingBaseProducts?: Array<string>
}

export interface CouponDetails {
  [key: string]: {
    id: string
    codeDiscount: {
      __typename: 'DiscountCodeBasic'
      codesCount: {
        count: number
      }
      shortSummary: string
    }
  }
}

export interface CartFulfillabilityState {
  canFulfillFromPreferred: boolean
  forcedLocation: Location | null
  routingChanged: boolean
  preferredLocation: Location | null
  fallbackLocation: Location | null
  strandedSkus: string[]
  skusBlockingPreferred: string[]
  inverseStrandedSkus: string[]
}

export const useShopifyStore = defineStore('shopify', {
  state: () => ({
    locale: useRoute().params.locale,
    cartIds: [] as CartIDRecord[],
    lastAddedItemSkus: [] as string[],
    lastAddedQuantities: {} as Record<string, number>, // Track how many of each SKU was added
    cart: {
      id: '',
      checkoutUrl: '',
      totalQuantity: null,
      buyerIdentity: {
        email: '',
        countryCode: '',
      },
      cost: {
        subtotalAmount: {
          amount: '',
          currencyCode: '',
        },
        totalAmount: {
          amount: '',
          currencyCode: '',
        },
        totalDutyAmount: {
          amount: '',
          currencyCode: '',
        },
        totalTaxAmount: {
          amount: '',
          currencyCode: '',
        },
        checkoutChargeAmount: {
          amount: '',
          currencyCode: '',
        },
      },
      deliveryGroups: {
        nodes: [],
      },
      lines: {
        edges: [],
      },
      attributes: [],
      discountCodes: [],
      discountAllocations: [],
      appliedGiftCards: [],
      createdAt: '',
    } as Cart,
    automaticDiscounts: [] as AutomaticDiscount[],
    automaticCodeDiscounts: [] as AutomaticCodeDiscountWithQualifyingProducts[],
    couponDetails: {} as CouponDetails,
    loading: false,
    fetched: false,
    optimisticCartCount: 0 as any,
    open: false,
    fulfillability: {
      canFulfillFromPreferred: true,
      forcedLocation: null as Location | null,
      routingChanged: false,
      preferredLocation: null as Location | null,
      fallbackLocation: null as Location | null,
      strandedSkus: [] as string[],
      skusBlockingPreferred: [] as string[],
      inverseStrandedSkus: [] as string[],
    } as CartFulfillabilityState,
    pendingModalTrigger: null as { skus: string[]; reason?: string } | null,
    reportedMisfireBundleIds: [] as string[],
  }),

  getters: {
    country(state) {
      let locale = state.locale || 'en-gb'

      if (Array.isArray(locale)) locale = locale[0]

      let countryCode = locale.split('-')[1].toUpperCase()

      // EU is a special case as it's not a country code but a region code
      if (countryCode === 'EU') countryCode = 'FR'

      // Falling back to countries[0] would price an unknown locale in whatever
      // country happens to sort first — Ascension Island, in USD.
      const country =
        countries.find((c) => c.isoCode === countryCode) ||
        countries.find((c) => c.isoCode === 'GB') ||
        countries[0]

      return country
    },

    isGlobaleManagedMarket(): boolean {
      return isGlobaleManagedMarket(this.country?.market)
    },

    cartId(): string | undefined {
      const marketId = this.country.market.id?.split('/').pop()
      const record = this.cartIds.find((r) => r.marketId === marketId)
      return record?.cartId
    },

    currencyCode(): string {
      if (!this.cart.totalQuantity) {
        // Until there are items in the cart it will report the incorrect
        // currency. Use the shop currency instead
        return this.country.currency.isoCode
      }

      return this.cart.cost.totalAmount.currencyCode
    },

    bundledCartItems(state) {
      if (!state.cart.totalQuantity) return {}

      const bundledCartItems = {} as { [key: string]: Bundle }

      this.cart.lines.edges.forEach((cartItem) => {
        if (cartItem.node.lineComponents?.length) {
          // This has been bundled by Shopify
          bundledCartItems[cartItem.node.id] = {
            isShopifyBundle: true,
            bundleId: cartItem.node.id,
            items: [
              cartItem,
              ...cartItem.node.lineComponents.map((component) => ({
                node: component,
              })),
            ] as { node: CartLine }[],
          }
        } else {
          let bundleId = cartItem.node.attributes.find((attribute) => attribute.key === '_bundleId')?.value

          if (!bundleId) bundleId = cartItem.node.id

          if (!bundledCartItems[bundleId]) {
            bundledCartItems[bundleId] = {
              isShopifyBundle: false,
              bundleId,
              items: [],
            }
          }

          bundledCartItems[bundleId].items.push(cartItem)
        }
      })

      Object.entries(bundledCartItems).forEach(([bundleId, bundle]) => {
        bundledCartItems[bundleId].summary = {
          bundleSku: getBundleSkuFromBundleItems(bundle.items),
          bundleSkus: getBundleSkusFromBundleItems(bundle.items),
          bundleBaseSku: getBundleBaseSkuFromBundleItems(bundle.items),
          bundleCost: getBundleCost(bundle),
          bundleOriginalCost: getOriginalBundleCost(bundle),
          bundleCostDifference: getBundleCost(bundle) - getOriginalBundleCost(bundle),
          bundleQuantity: getBundleQuantity(bundle.items),
          bundleDisplayOrder: getBundleDisplayOrder(bundle.items),
          bundleCheckoutInfo: getBundleCheckoutInfo(bundle.items),
          bundleDespatchDate: getBundleDespatchDate(bundle.items),
          bundleIsGiftCard: getBundleIsGiftCard(bundle.items),
          bundleIsUpsoldItem: getBundleUpsellItem(bundle.items),
          bundleIsPrescription: getBundleIsPrescription(bundle.items),
          bundleIsPrebuiltVariant: bundle.items.some((item) =>
            item.node.attributes.some((attribute) => attribute.key === '_prebuiltSku')
          ),
          bundleParentSku: getParentBundleSku(bundle.items),
          bundleParentBaseSku: getParentBundleBaseSku(bundle.items),
          bundleUsesTheme: getUsesTheme(bundle.items),
          bundleExpectedFree: getBundleExpectedFree(bundle.items),
        }
      })

      Object.entries(bundledCartItems).forEach(([bundleId, _bundle]) => {
        if (bundledCartItems[bundleId].summary.bundleParentSku) {
          const matchingParentBundle = Object.values(bundledCartItems).find((bundleNested) => {
            return bundleNested?.summary?.bundleSku === bundledCartItems[bundleId].summary?.bundleParentSku
          })

          if (matchingParentBundle) {
            bundledCartItems[bundleId].summary.bundleDisplayOrder =
              matchingParentBundle.summary.bundleDisplayOrder + 0.5
          }
        }
      })

      return bundledCartItems
    },

    upsoldItems(): any {
      return Object.entries(this.bundledCartItems)
        .filter(([_bundleId, bundle]) => bundle.summary.bundleIsUpsoldItem === true)
        .map(([_bundleId, bundle]) => bundle)
    },

    baseSkusInCart(): any {
      return Object.entries(this.bundledCartItems).map(([_bundleId, bundle]) => bundle.summary.bundleBaseSku)
    },

    hasFreeItemInCart(): boolean {
      return Object.entries(this.bundledCartItems).some(([_bundleId, bundle]) => {
        return bundle.summary.bundleCost === 0
      })
    },

    bundledCartItemsTotal(state) {
      return state.cart.totalQuantity
    },

    getOptimisticCartCount(state): number {
      return state.optimisticCartCount !== this.bundledCartItemsTotal
        ? state.optimisticCartCount
        : this.bundledCartItemsTotal
    },

    freeShippingPromo(): AutomaticDiscount | undefined {
      let freeShippingPromo: AutomaticDiscount | AutomaticDiscount[] | undefined

      const marketAvailableDiscounts = this.automaticDiscounts.filter((automaticDiscount) =>
        isDiscountAvailableInMarket(automaticDiscount, this.country.market.id)
      )

      // First attempt to find a shipping promo for a specific country
      const freeShippingPromoForCountry: AutomaticDiscount | undefined = marketAvailableDiscounts.find(
        (automaticDiscount) => {
          if (
            automaticDiscount.automaticDiscount.discountClass === 'SHIPPING' &&
            automaticDiscount.automaticDiscount.destinationSelection?.type === 'DiscountCountries'
          ) {
            if (automaticDiscount.automaticDiscount.destinationSelection.countries?.length) {
              return automaticDiscount.automaticDiscount.destinationSelection.countries.includes(
                this.locale.split('-')[1].toUpperCase()
              )
            }

            return true
          }
        }
      )

      // If there is no promo for a country then just get the first free shipping promo
      if (!freeShippingPromoForCountry) {
        freeShippingPromo = marketAvailableDiscounts.filter(
          (automaticDiscount) =>
            automaticDiscount.automaticDiscount.discountClass === 'SHIPPING' &&
            automaticDiscount.automaticDiscount.destinationSelection?.type === 'DiscountCountryAll'
        )

        if (freeShippingPromo.length > 1) {
          freeShippingPromo = freeShippingPromo.sort((a, b) => {
            return (
              Number(a.automaticDiscount?.minimumRequirement?.greaterThanOrEqualToSubtotal?.amount ?? 0) -
              Number(b.automaticDiscount?.minimumRequirement?.greaterThanOrEqualToSubtotal?.amount ?? 0)
            )
          })[0]
        } else {
          freeShippingPromo = freeShippingPromo[0]
        }
      } else {
        freeShippingPromo = freeShippingPromoForCountry
      }

      // Add additional pre-calculated fields to the promo based on other field values
      if (freeShippingPromo) {
        const thresholdAmount = Number(
          freeShippingPromo.automaticDiscount?.minimumRequirement?.greaterThanOrEqualToSubtotal?.amount || 0
        )

        freeShippingPromo.remainingSpend =
          thresholdAmount - Number(this.cart.cost.totalAmount.amount) + this.totalShippingCost

        freeShippingPromo.thresholdSummary = {
          amount: thresholdAmount,
          thresholdForDiscountMet: freeShippingPromo.remainingSpend <= 0,
        }

        freeShippingPromo.discountSummary = {
          maxShippingPrice: freeShippingPromo.automaticDiscount.maximumShippingPrice,
          discountType: freeShippingPromo.automaticDiscount.discountClass,
        }

        freeShippingPromo.showFreeShippingMessage =
          freeShippingPromo?.discountSummary?.maxShippingPrice?.amount &&
          this.locale.split('-')[1].toUpperCase() === 'GB'
      }

      return freeShippingPromo
    },

    getDiscountsFromProduct() {
      return (
        customisedProduct: any | null,
        staticProductData?: {
          sku: string
          price: number
        }
      ) => {
        return getAllDiscountsFromProduct(
          customisedProduct,
          getAllValidGiftDiscounts(
            this.automaticDiscounts,
            this.automaticCodeDiscounts,
            this.country.market.id
          ),
          this.cart,
          staticProductData
        )
      }
    },

    getDiscountsForProductFromCartItems() {
      return (
        customisedProduct: any,
        staticProductData?: {
          sku: string
        }
      ) => {
        return getDiscountsFromCartItems({
          customisedProduct,
          bundledCartItems: this.bundledCartItems,
          automaticDiscounts: this.automaticDiscounts,
          automaticCodeDiscounts: this.automaticCodeDiscounts,
          cartSubtotal: Number(this.cart.cost.subtotalAmount.amount),
          staticProductData,
          activeMarketId: this.country.market.id,
        })
      }
    },

    couponCodes(state) {
      return state.cart.discountCodes?.map((discount) => discount.code) || []
    },

    applicableCouponCodes(state) {
      return (
        state.cart.discountCodes
          ?.filter((discount) => discount.applicable)
          .map((discount) => discount.code) || []
      )
    },

    totalShippingCost(state): number {
      return Number(state.cart.deliveryGroups.nodes[0]?.selectedDeliveryOption?.estimatedCost?.amount) || 0
    },

    totalDiscount(state): number {
      const discountAmounts = state.cart.discountAllocations.map((discount: any) =>
        Number(discount.discountedAmount.amount)
      )
      // Filter out discounts that exactly match the cost of shipping
      const filteredDiscounts = discountAmounts.filter((discount) => discount !== this.totalShippingCost)
      return filteredDiscounts.reduce((sum, number) => sum + number, 0)
    },

    hasFreeShippingDiscount(state) {
      const discountAmounts = state.cart.discountAllocations.map((discount: any) =>
        Number(discount.discountedAmount.amount)
      )
      return discountAmounts.some((discount) => discount === this.totalShippingCost)
    },

    giftCard(state): GiftCard {
      return state.cart.appliedGiftCards[0]
    },

    existingBundlesInCartFromSku() {
      return (sku: string) => {
        return getExistingBundlesInCartFromSku(this.bundledCartItems, sku)
      }
    },
  },

  actions: {
    /**
     * Update cart fulfillability based on current cart items and customer location
     * Checks if all items can be fulfilled from preferred location or need fallback
     */
    async updateCartFulfillability({ triggerModal = false, triggerSkus = [] as string[] } = {}) {
      try {
        const countryCode = this.country.isoCode as string

        if (!countryCode) {
          console.warn('Cannot update cart fulfillability: no country code available')
          return
        }

        if (Object.keys(this.bundledCartItems).length === 0) {
          this.fulfillability = {
            canFulfillFromPreferred: true,
            forcedLocation: null,
            routingChanged: false,
            preferredLocation: null,
            fallbackLocation: null,
            strandedSkus: [],
            skusBlockingPreferred: [],
            inverseStrandedSkus: [],
          }
          return
        }

        const { $product } = useNuxtApp() as any
        const { getCartFulfillability, getCartItemAvailableForSale, isFulfilledFromUKOnly } =
          await import('~/composables/useCartFulfillment')

        // One routing unit per bundle (a custom pair is many lines but one product). Reconstruct
        // each as CartItem.vue does — passing the bundle summary and PDP config — so the model's
        // type getters (isPrebuiltVariant / isCustom) resolve, rather than defaulting to UK-only.
        const bundles = Object.values(this.bundledCartItems as Record<string, any>)

        const cartItemsWithProducts = await Promise.all(
          bundles.map(async (bundle: any) => {
            const sku = bundle.summary.bundleSku
            try {
              const customisedProduct = await $product.getCustomisedProduct(
                bundle.summary.bundleBaseSku,
                {
                  sku,
                  lensTechSelected: true,
                  isPrebuiltVariant: bundle.summary.bundleIsPrebuiltVariant,
                },
                true
              )
              return {
                // Keyed by bundle SKU so strandedSkus / skusBlockingPreferred map back to removable lines.
                sku,
                quantity: bundle.summary.bundleQuantity,
                locationInventory: customisedProduct.product.locationInventory?.[customisedProduct.sku] || {},
                // Has a next-delivery-date — NOT isOnPreorder, which is gated on being globally OOS
                // and so reads false for a line stocked elsewhere that's still preorderable at OCF.
                preorderEligible: !!customisedProduct.preorderDate,
                availableForSale: getCartItemAvailableForSale(customisedProduct),
                fulfilledFromUKOnly: isFulfilledFromUKOnly(customisedProduct),
              }
            } catch (err) {
              console.error('❌ [Store] Error fetching product:', err)
              return null
            }
          })
        )

        const cartItems = cartItemsWithProducts.filter(Boolean) as Array<{
          sku: string
          quantity: number
          locationInventory: Record<string, number>
          preorderEligible: boolean
          availableForSale: boolean
          fulfilledFromUKOnly: boolean
        }>

        const previousFulfillability = this.fulfillability.preferredLocation ? this.fulfillability : null
        // triggerSkus are bundle SKUs, matching cartItems' identity directly.
        const result = await getCartFulfillability(
          cartItems,
          countryCode,
          previousFulfillability,
          triggerSkus
        )

        this.fulfillability = {
          canFulfillFromPreferred: result.canFulfillFromPreferred,
          forcedLocation: result.forcedLocation,
          routingChanged: result.routingChanged,
          preferredLocation: result.preferredLocation,
          fallbackLocation: result.fallbackLocation,
          strandedSkus: result.strandedSkus,
          skusBlockingPreferred: result.skusBlockingPreferred,
          inverseStrandedSkus: result.inverseStrandedSkus,
        }
        // Surface the modal on any add that changes routing or strands lines, not only when we captured added SKUs.
        if (triggerModal && (triggerSkus.length || result.routingChanged || result.strandedSkus.length)) {
          this.pendingModalTrigger = { skus: triggerSkus }
        }
      } catch (error) {
        console.error('Failed to update cart fulfillability:', error)
      }
    },

    async createCart() {
      this.loading = true

      try {
        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartCreateResponse>('create-cart', createCartQuery, {
          variables: { attributes: getExpectedCartAttributes() },
        })

        if (response?.cartCreate?.cart) {
          this.cart = response.cartCreate.cart
        }

        const marketId = this.country.market.id?.split('/').pop() as string
        this.cartIds = [
          ...this.cartIds.filter((r) => r.marketId !== marketId),
          { marketId, cartId: this.cart.id },
        ]
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
        this.optimisticCartCount = this.cart.totalQuantity || 0
      }
    },

    async getCart() {
      if (!this.cartId) {
        await this.createCart()
      }

      // createCart() catches and swallows its own exceptions, so cartId may
      // still be undefined if the cart creation request failed — avoid sending
      // an invalid ID to Shopify
      if (!this.cartId) return

      try {
        this.loading = true

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartResponse>('get-cart', getCart, {
          variables: { id: this.cartId },
        })

        if (response?.cart) {
          this.cart = response.cart
          this.optimisticCartCount = this.cart.totalQuantity || 0

          await this.validateCart()
          await this.validateCartAttributes()
          await this.updateCartFulfillability()

          this.checkForFreeGiftMisfires()
        } else {
          await this.createCart()
        }

        this.fetched = true
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
      }
    },

    async validateCart() {
      let isValid = true

      // If the countryCode of this cart doesn't match the
      // current country, we need to create a new cart
      if (this.country.isoCode !== this.cart?.buyerIdentity?.countryCode) {
        console.error(
          `Cart country code ${this.cart?.buyerIdentity?.countryCode} does not match current country ${this.country.isoCode}`
        )
        isValid = false
      }

      if (!isValid) {
        await this.createCart()
      }
    },

    async validateCartAttributes() {
      // Check if the cart has the expected attributes
      const expectedAttributes = getExpectedCartAttributes(this.cart)
      const cartAttributes = this.cart.attributes

      // Ensure that all expected attributes are present in the cart
      let hasExpectedAttributes = expectedAttributes.every((expected) => {
        return cartAttributes.some(
          (attribute) => attribute.key === expected.key && attribute.value === expected.value
        )
      })

      // Ensure that all cart attributes are present in the expected attributes
      hasExpectedAttributes =
        hasExpectedAttributes &&
        cartAttributes.every((cartAttribute) => {
          return expectedAttributes.some(
            (expected) => expected.key === cartAttribute.key && expected.value === cartAttribute.value
          )
        })

      if (!hasExpectedAttributes) {
        await this.updateCartAttributes(expectedAttributes)
      }
    },

    checkForFreeGiftMisfires() {
      const misfiredBundles = getMisfiredFreeGiftBundles(this.bundledCartItems)

      misfiredBundles.forEach((bundle) => {
        if (this.reportedMisfireBundleIds.includes(bundle.bundleId)) return
        this.reportedMisfireBundleIds.push(bundle.bundleId)

        const expectedDiscount = findExpectedFreeDiscountForGiftBundle(bundle, this.automaticDiscounts)

        captureException(
          new Error(
            `Free gift misfire: "${bundle.summary.bundleSku}" was offered as free but resolved with cost ${bundle.summary.bundleCost}`
          ),
          {
            tags: {
              market: this.country.market.handle,
              discount: expectedDiscount?.automaticDiscount.title || 'unknown',
            },
            extra: {
              marketId: this.country.market.id,
              discountId: expectedDiscount?.id,
              giftSku: bundle.summary.bundleSku,
              parentSku: bundle.summary.bundleParentSku,
              bundleCost: bundle.summary.bundleCost,
              cartId: this.cart.id,
            },
          }
        )
      })
    },

    async updateCartAttributes(attributes: CartAttributes) {
      if (!this.cartId) await this.createCart()

      try {
        this.loading = true

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartAttributesUpdateResponse>(
          'update-cart-attributes',
          cartAttributesUpdate,
          {
            variables: {
              cartId: this.cartId,
              attributes,
            },
          }
        )

        if (response?.cartAttributesUpdate?.cart) {
          this.cart = response.cartAttributesUpdate.cart
        }
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
      }
    },

    async addCartItems(
      newLines: {
        merchandiseId: string
        quantity: number
        attributes: {
          key: string
          value: string
        }[]
      }[],
      updateCartState: boolean = true,
      updateOptimisticCount: boolean = true
    ) {
      if (updateOptimisticCount) {
        this.optimisticCartCount =
          this.optimisticCartCount +
          (getUniqueBundleIdsFromNewLineItems(newLines).length
            ? getUniqueBundleIdsFromNewLineItems(newLines).length
            : 1)
      }

      if (!this.cartId) await this.createCart()

      try {
        this.loading = true

        const newLinesWithBundleDisplayOrder = buildCartItemsWithBundleDisplayOrder(
          this.bundledCartItems,
          newLines
        )

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartLinesAddResponse>('add-items-to-cart', addLines, {
          variables: {
            cartId: this.cartId,
            lines: newLinesWithBundleDisplayOrder,
          },
        })

        // Store cart if it is defined
        if (response?.cartLinesAdd?.cart && updateCartState) {
          this.cart = response.cartLinesAdd.cart
          this.optimisticCartCount = this.cart.totalQuantity || 0

          const addedMerchandiseIds = newLines.map((item) => item.merchandiseId)
          const matchedLines = this.cart.lines.edges.filter(
            (edge) => edge.node.merchandise.id && addedMerchandiseIds.includes(edge.node.merchandise.id)
          )

          // Key added items by bundle SKU (what removal uses), not component merchandise SKU —
          // a custom build's component lines have no SKU, so keying off those loses the item.
          const addedSkus = [
            ...new Set(
              matchedLines
                .map((edge) => {
                  const bundle = Object.values(this.bundledCartItems as Record<string, any>).find(
                    (candidate) => candidate.items.some((item: any) => item.node.id === edge.node.id)
                  )
                  return bundle?.summary?.bundleSku
                })
                .filter((sku): sku is string => Boolean(sku))
            ),
          ]

          const addedQuantities: Record<string, number> = {}
          newLines.forEach((item) => {
            const matchedLine = matchedLines.find((edge) => edge.node.merchandise.id === item.merchandiseId)
            if (matchedLine?.node.merchandise.sku) {
              addedQuantities[matchedLine.node.merchandise.sku] = item.quantity
            }
          })

          this.lastAddedItemSkus = addedSkus
          this.lastAddedQuantities = addedQuantities

          await this.validateCart()
          await this.validateCartAttributes()
          await this.updateCartFulfillability({ triggerModal: true, triggerSkus: addedSkus })

          this.checkForFreeGiftMisfires()

          this.fetched = true
        }

        /**
         *
         * Check if a product that qualifies for an automatic code discount
         * is already in cart and if so add the code to the cart.
         *
         */
        const availableCartDiscounts = getDiscountsFromCartItems({
          bundledCartItems: this.bundledCartItems,
          automaticDiscounts: this.automaticDiscounts,
          automaticCodeDiscounts: this.automaticCodeDiscounts,
          cartSubtotal: Number(this.cart.cost.subtotalAmount.amount),
          activeMarketId: this.country.market.id,
        })

        if (availableCartDiscounts?.length) {
          const missingCartCodes: Array<string> = getMissingCartCodesFromAvailableDiscounts(
            availableCartDiscounts,
            this.cart.discountCodes
          )

          if (missingCartCodes?.length) {
            const addedCodesResponses = await Promise.all(
              missingCartCodes.map((missingCartCode) =>
                this.applyCouponCode({ code: missingCartCode, silent: true })
              )
            )

            const lastAddedCodeResponse = addedCodesResponses[addedCodesResponses?.length - 1]

            if (lastAddedCodeResponse?.body?.cartDiscountCodesUpdate?.cart) {
              this.cart = lastAddedCodeResponse?.body?.cartDiscountCodesUpdate?.cart
              this.optimisticCartCount = this.cart.totalQuantity || 0

              return lastAddedCodeResponse
            }
          }
        }

        return response
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
      }
    },

    clearPendingModalTrigger() {
      this.pendingModalTrigger = null
    },

    /** Remove the lines that can't ship from where the cart now routes (newest-wins). */
    async removeStrandedItems() {
      const strandedSkus = [...(this.fulfillability.strandedSkus || [])]
      if (!strandedSkus.length) return

      await this.removeCartItems(strandedSkus, true, true)
      await this.updateCartFulfillability()
    },

    /**
     * Remove the lines that forced the cart off its preferred location (location-change modal's
     * "Remove"). Keys off bundle SKUs in fulfilment state, so it works for any product type.
     */
    async removeItemsBlockingPreferred() {
      const skus = [...(this.fulfillability.skusBlockingPreferred || [])]
      if (!skus.length) return

      await this.removeCartItems(skus, true, true)
      await this.updateCartFulfillability()
    },

    /** Keep the stranded lines and remove the ones that conflict with them instead. */
    async keepStrandedItems() {
      const inverseStrandedSkus = [...(this.fulfillability.inverseStrandedSkus || [])]
      if (!inverseStrandedSkus.length) return

      await this.removeCartItems(inverseStrandedSkus, true, true)
      await this.updateCartFulfillability()
    },

    async removeCartItems(
      productsToRemove: string[],
      updateCartState: boolean = true,
      updateOptimisticCart: boolean = true
    ) {
      if (!this.cartId) return

      if (updateOptimisticCart) {
        this.optimisticCartCount = this.optimisticCartCount - productsToRemove.length
      }

      if (!Array.isArray(productsToRemove)) {
        captureException(new Error('productsToRemove should be an array of SKUs'))
        return
      }

      const lineIdsToRemove = buildLineIdsToRemove(productsToRemove, this.bundledCartItems)

      if (!lineIdsToRemove.length) {
        captureException(
          new Error(
            `Could not find matching bundle item to remove based on passed in SKU(s) ${productsToRemove}`
          )
        )
        return
      }

      try {
        this.loading = true

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartLinesRemoveResponse>('remove-lines', removeLines, {
          variables: {
            cartId: this.cartId,
            lineIds: lineIdsToRemove,
          },
        })

        /**
         *
         * Update cart state from response.
         *
         * Do this before anything else so subsequent functions can use the
         * updated cart state.
         *
         */
        if (response?.cartLinesRemove?.cart && updateCartState) {
          this.cart = response.cartLinesRemove.cart
          this.optimisticCartCount = this.cart.totalQuantity || 0

          await this.validateCart()
          await this.validateCartAttributes()
          await this.updateCartFulfillability({ triggerModal: true, triggerSkus: productsToRemove })

          this.checkForFreeGiftMisfires()
        }

        if (!this.automaticCodeDiscounts?.length) {
          return response
        } else if (this.automaticCodeDiscounts?.length) {
          const disqualifiedCouponCodes = getDisqualifiedCouponCodes(
            this.automaticCodeDiscounts,
            this.bundledCartItems,
            this.cart.discountCodes
          )

          if (disqualifiedCouponCodes?.length) {
            const couponRemovalResponse = await this.removeCouponCode({
              codes: disqualifiedCouponCodes,
              silent: true,
            })

            if (couponRemovalResponse?.body?.cartDiscountCodesUpdate?.cart) {
              this.cart = couponRemovalResponse?.body?.cartDiscountCodesUpdate?.cart
              this.optimisticCartCount = this.cart.totalQuantity || 0
            }

            return couponRemovalResponse
          }
        }

        return response
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
      }
    },

    async updateCartItems(
      lines:
        | {
            id: string
            merchandiseId?: string
            quantity: number
          }
        | { id: string; quantity: number }[]
    ) {
      if (!this.cartId) return

      try {
        this.loading = true

        const linesArray = Array.isArray(lines) ? lines : [lines]
        const previousQuantities: Record<string, number> = {}
        const skusByLineId: Record<string, string> = {}

        this.cart?.lines?.edges.forEach((edge) => {
          const sku = edge.node.merchandise.sku
          if (sku) {
            previousQuantities[sku] = edge.node.quantity
            skusByLineId[edge.node.id] = sku
          }
        })

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartLinesUpdateResponse>('update-lines', updateLines, {
          variables: {
            cartId: this.cartId,
            lines,
          },
        })

        // Store cart if it is defined
        if (response?.cartLinesUpdate?.cart) {
          this.cart = response.cartLinesUpdate.cart
          this.optimisticCartCount = this.cart.totalQuantity || 0

          const changedSkus: string[] = []
          const quantityChanges: Record<string, number> = {}

          linesArray.forEach((line) => {
            const sku = skusByLineId[line.id]
            if (sku) {
              const previousQty = previousQuantities[sku] || 0
              const newQty = line.quantity
              const diff = newQty - previousQty

              if (diff > 0) {
                changedSkus.push(sku)
                quantityChanges[sku] = diff
              }
            }
          })

          if (changedSkus.length > 0) {
            this.lastAddedItemSkus = changedSkus
            this.lastAddedQuantities = quantityChanges
          }

          await this.validateCart()
          await this.validateCartAttributes()
          await this.updateCartFulfillability({ triggerModal: true, triggerSkus: changedSkus })

          this.checkForFreeGiftMisfires()
        }

        return response
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
      }
    },

    async swapCartItems(
      itemsToAdd: {
        merchandiseId: string
        quantity: number
      }[],
      productsToRemove: string[]
    ) {
      if (!this.cartId) return

      try {
        this.loading = true

        await this.removeCartItems(productsToRemove, false, false)
        return await this.addCartItems(itemsToAdd, true, false)
      } catch (error) {
        captureException(error as Error)
      } finally {
        this.loading = false
      }
    },

    async getAutomaticDiscounts(options: { refresh: boolean } = { refresh: false }) {
      if (!options.refresh && preBuiltAutomaticDiscounts) {
        if (preBuiltAutomaticDiscounts.data) {
          this.automaticDiscounts = preBuiltAutomaticDiscounts.data?.automaticDiscountNodes
            ?.nodes as AutomaticDiscount[]
        }
      }

      // If calling with forced refresh or if statically imported data above is not returned
      // for some reason fallback to querying the shopify Admin API directly
      if (options.refresh || (!preBuiltAutomaticDiscounts.data && !this.automaticDiscounts?.length)) {
        const { data, error } = await useFetch<AutomaticDiscount[]>(
          '/api/' + useRoute().params.locale + '/promotions'
        )

        if (data.value) {
          this.automaticDiscounts = data.value
        }

        if (error.value) {
          captureException(error.value as Error)
          return
        }
      }
    },

    async getAutomaticCodeDiscounts(options: { refresh: boolean } = { refresh: false }) {
      if (!options.refresh && preBuiltAutomaticCodeDiscounts) {
        if (preBuiltAutomaticCodeDiscounts.data) {
          this.automaticCodeDiscounts = preBuiltAutomaticCodeDiscounts?.data?.codeDiscountNodes
            ?.nodes as DiscountCodeBasic[]
        }
      }

      // If calling with forced refresh or if statically imported data above is not returned
      // for some reason fallback to querying the shopify Admin API directly
      if (options.refresh || (!preBuiltAutomaticCodeDiscounts.data && !this.automaticCodeDiscounts?.length)) {
        const { data, error } = await useFetch<DiscountCodeBasic[]>(
          '/api/' + useRoute().params.locale + '/automatic-code-discounts'
        )

        if (data.value) {
          this.automaticCodeDiscounts = data.value
        }

        if (error.value) {
          captureException(error.value as Error)
          return
        }

        if (data.value) {
          this.automaticCodeDiscounts = data.value
        }

        if (error.value) {
          captureException(error.value as Error)
          return
        }
      }

      if (!this.automaticCodeDiscounts?.length) return

      /**
       *
       * Inject the requiredProductsInCart base skus as an array into the matching Shopify Discount code
       * so that we can later compare which items should be in cart for an auto applied discount code to
       * qualify the product in question for discount.
       *
       * 1. Get the discount models from Dato.
       *
       * 2. Find matching discount code.
       *
       * 3. Pull baseSkus from dato response and merge into the discount code to be used later in
       * the getDiscountsFromCartItems.
       *
       */
      const { data: autoAppliedDiscountCodesDatoResponse } = await useDatoQuery({
        query: autoAppliedDiscountCodesQuery,
        prefetchedRequest: 'autoAppliedDiscountCodes',
      })

      if (!autoAppliedDiscountCodesDatoResponse.value?.allAutoAppliedProductDiscountCodes?.length) return

      this.automaticCodeDiscounts = this.automaticCodeDiscounts.map((automaticCodeDiscount) => {
        // find the matching code on the dato model
        const matchedDatoCode =
          autoAppliedDiscountCodesDatoResponse.value.allAutoAppliedProductDiscountCodes.find(
            (datoDiscountCodeResult) => {
              return automaticCodeDiscount.codeDiscount.codes.nodes.find((codeNode) =>
                codeNode.code.includes(datoDiscountCodeResult.discountCode.discountCodeId)
              )
            }
          )

        let qualifyingBaseProducts = []

        if (matchedDatoCode) {
          qualifyingBaseProducts = matchedDatoCode.requiredProductsInCart.map(
            (baseProduct: string) => baseProduct.baseSku
          )
        }

        return {
          ...automaticCodeDiscount,
          qualifyingBaseProducts: qualifyingBaseProducts.length ? qualifyingBaseProducts : [],
        }
      })
    },

    async updateCouponCodes(codes: string[]): Promise<CouponResponse> {
      try {
        this.loading = true

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartDiscountCodesUpdateResponse>(
          'update-discounts',
          cartDiscountCodesUpdate,
          {
            variables: {
              cartId: this.cartId,
              discountCodes: codes,
            },
          }
        )

        if (response?.cartDiscountCodesUpdate?.cart) {
          this.cart = response.cartDiscountCodesUpdate.cart
        } else {
          throw new Error('Unable to update discount codes')
        }
        return { success: true, body: response }
      } catch (error) {
        captureException(error as Error)
        return { success: false }
      } finally {
        this.loading = false
      }
    },

    async applyCouponCode({ code, silent = false }: { code: string; silent?: boolean }) {
      const alertStore = useAlertStore()
      if (!this.cartId) await this.createCart()

      // Check if coupon is a gift card first
      const isGiftCard = await isValidGiftCard(code)
      if (isGiftCard) {
        const giftCard = await this.updateGiftCard({ code })
        return { success: giftCard.success }
      }

      const isValidCode = await this.validateCouponCode(code)
      if (!isValidCode) {
        alertStore.ADD_ERROR_MESSAGE({
          body: 'Invalid coupon code. Please try again.',
        })
        return {
          success: false,
          error: 'invalid',
        }
      }

      if (this.couponCodes.includes(code)) {
        if (!silent) {
          alertStore.ADD_MESSAGE({
            body: `Coupon code ${code} already applied.`,
          })
        }
        return {
          success: true,
        }
      }

      try {
        const response = await this.updateCouponCodes([...this.couponCodes, code])

        if (!response) {
          throw new Error(`Unable to add discount code: ${code}`)
        }

        if (response && this.couponCodes.includes(code)) {
          if (!silent) {
            alertStore.ADD_MESSAGE({
              body: `Coupon code ${code} successfully applied.`,
            })
          }
          return { body: response, success: true }
        }
      } catch (error) {
        alertStore.ADD_ERROR_MESSAGE({
          body: 'Something went wrong. Please try again.',
        })
        captureException(error as Error)
        return { success: false, error: `${error}` }
      }
    },

    async removeCouponCode({ codes, silent = false }: { codes: Array<string>; silent?: boolean }) {
      const alertStore = useAlertStore()

      /**
       *
       * Find the applied discount codes on the cart and match with the code
       * passed in. Exclude any passed in discount codes that have not been
       * been applied to the cart.
       *
       */

      const appliedCodesToRemove = this.couponCodes.filter((appliedCouponCode) => {
        if (appliedCouponCode === codes.find((code) => appliedCouponCode === code)) return true
      })

      if (!appliedCodesToRemove?.length) {
        console.error(`The discount code(s) ${codes.join()} are not currently applied`)
        return { success: false }
      }

      this.loading = true

      try {
        const codesToKeep =
          this.couponCodes
            ?.map((appliedCode) => {
              if (!appliedCodesToRemove.find((appliedCodeToRemove) => appliedCodeToRemove === appliedCode)) {
                return appliedCode
              }

              return ''
            })
            .filter((a) => a) || []

        const response = await this.updateCouponCodes([...codesToKeep])

        if (
          response &&
          !this.couponCodes.find((appliedCode) => appliedCode === codes.find((code) => code === appliedCode))
        ) {
          if (!silent) {
            alertStore.ADD_MESSAGE({
              body: `Coupon code ${codes.join(', ')} successfully removed.`,
            })
          }
          this.loading = false
          return { ...response, success: true }
        } else {
          alertStore.ADD_ERROR_MESSAGE({
            body: `We were unable to remove that code, please try again.`,
          })
          this.loading = false
          return { success: false }
        }
      } catch (error) {
        alertStore.ADD_ERROR_MESSAGE({
          body: `Something went wrong, please try again.`,
        })
        captureException(error as Error)
        this.loading = false
        return { success: false }
      }
    },

    async validateCouponCode(code: string): Promise<boolean> {
      if (this.couponDetails[code]?.id) return true
      const coupon = await getCouponCode(code)
      // Store the coupon if it is valid
      if (coupon?.id) {
        this.couponDetails = {
          ...this.couponDetails,
          [code]: coupon,
        }
      }
      return coupon?.id ? true : false
    },

    async updateGiftCard({
      code,
      silent = true,
    }: {
      code: string
      silent?: boolean
    }): Promise<couponResponse> {
      const alertStore = useAlertStore()
      try {
        this.loading = true

        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<CartGiftCardCodesUpdateResponse>(
          'update-giftcards',
          cartGiftCardCodesUpdate,
          {
            variables: {
              cartId: this.cartId,
              giftCardCodes: [code],
            },
          }
        )
        if (response?.cartGiftCardCodesUpdate?.cart) {
          this.cart = response.cartGiftCardCodesUpdate.cart
          if (!silent) {
            alertStore.ADD_MESSAGE({
              body: `Gift card successfully updated`,
            })
          }
          return { success: true }
        } else {
          throw new Error('Unable to update gift cards')
        }
      } catch (error) {
        alertStore.ADD_ERROR_MESSAGE({
          body: `Something went wrong, please try again.`,
        })
        captureException(error as Error)
        return { success: false }
      } finally {
        this.loading = false
      }
    },

    async getGiftCardProduct(commerceId: string) {
      try {
        const { shopifyRequest } = useShopifyClient()
        const response = await shopifyRequest<GetGiftCardProductResponse>(
          'get-gift-card-product',
          getGiftCardProduct,
          { variables: { id: commerceId, country: 'US' } }
        )

        if (response?.product?.variants?.nodes?.length) {
          return {
            giftCardOptions: response.product.variants.nodes.map((option) => option),
          }
        }
      } catch (error) {
        captureException(error as Error)
      }
    },

    toggleCart(boolean: boolean) {
      this.open = boolean
    },

    checkActivePromoById(id: string) {
      return (
        !!this.automaticDiscounts.find((discount) => discount.id.includes(id)) ||
        !!this.automaticCodeDiscounts.find((discount) => discount.id.includes(id))
      )
    },

    addGlobalPromoData(promo: any) {
      const discountIds = promo.shopifyPromotionId.split(',')
      discountIds.forEach((id) => {
        const linkedDiscount = this.automaticDiscounts.find((discount) => discount.id.includes(id))
        if (linkedDiscount) {
          linkedDiscount.discountSummary = {
            ...linkedDiscount.discountsummary,
            specialTheme: promo.theme?.type === 'special',
            tagText: promo.productTagText,
          }
        }
      })
    },
  },

  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
    pick: ['cartIds', 'optimisticCartCount'],
  },

  hydrate(state) {
    state.locale = useRoute().params.locale
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useShopifyStore, import.meta.hot))
}
